/**
 * ehr#743 (PROFILE_TABS_SEARCH_10-30-V11, Jay 22b): the client chart's Profile has its own
 * Referral section, split out of "Employment & Referral". Its Referral source dropdown lists the
 * practice's Settings > Referral Sources (`referral_sources`, GET /lookups/referral-sources).
 */
/**
 * V11's referral sources, in V11's order. A migration gives every practice the ones it is
 * missing (matched by name, any case); a new practice gets them when it is provisioned.
 */
export const DEFAULT_REFERRAL_SOURCES = [
    'Self',
    'Physician',
    'Psychology Today',
    'Insurance directory',
    'School',
    'Employer / EAP',
    'Court / probation',
    'Friend or family',
    'Other',
];
/** The names in `defaults` that `existing` does not already hold (trimmed, any case). */
export function missingReferralSources(existing, defaults = DEFAULT_REFERRAL_SOURCES) {
    const have = new Set(existing.map((n) => n.trim().toLowerCase()));
    return defaults.filter((n) => !have.has(n.toLowerCase()));
}
/**
 * The Referral source dropdown's options: the practice's list with V11's defaults first (in V11's
 * order) and the practice's own names after them (as the lookup returns them, A to Z), "Other"
 * last so its write-in stays at the end. A value saved before that is no longer on the list (an
 * old built-in choice, or a source deleted in Settings) stays as an option so the chart still
 * shows it.
 */
export function referralSourceOptions(names, current = '') {
    const seen = new Set();
    const out = [];
    const push = (n) => {
        const k = n.trim().toLowerCase();
        if (!k || seen.has(k))
            return;
        seen.add(k);
        out.push(n.trim());
    };
    const byKey = new Map(names.map((n) => [n.trim().toLowerCase(), n]));
    for (const d of DEFAULT_REFERRAL_SOURCES) {
        if (d !== 'Other' && byKey.has(d.toLowerCase()))
            push(byKey.get(d.toLowerCase()));
    }
    for (const n of names)
        if (n.trim().toLowerCase() !== 'other')
            push(n);
    if (byKey.has('other'))
        push(byKey.get('other'));
    if (current.trim() && !seen.has(current.trim().toLowerCase()))
        out.push(current.trim());
    return out;
}
/** A Referring provider NPI is blank or exactly 10 digits (V11: red until it has 10). */
export function referralNpiValid(v) {
    const t = v.trim();
    return t === '' || /^\d{10}$/.test(t);
}
/** Keys that moved from the old `employment` section to `referral` (ehr#743). */
export const MOVED_TO_REFERRAL = ['referralSource', 'referralSourceOther'];
/**
 * Records saved before the split keep the referral source under `employment.*`. On load it moves
 * to `referral.*` (never over a value already in `referral`) and leaves `employment`, so the next
 * save writes the new layout. A record with nothing to move comes back as it was.
 */
export function migrateReferral(d) {
    const emp = d.employment;
    if (!emp || !MOVED_TO_REFERRAL.some((k) => k in emp))
        return d;
    const referral = { ...(d.referral ?? {}) };
    const employment = { ...emp };
    // A referral source already in `referral` is newer: keep it (and its own write-in) as it is.
    const keep = !!(referral.referralSource ?? '').trim();
    for (const k of MOVED_TO_REFERRAL) {
        const v = employment[k];
        if (v && !keep)
            referral[k] = v;
        delete employment[k];
    }
    return { ...d, employment, referral };
}
/**
 * ehr#743 (Q3, coordinator 10-31): a client added with a referral source (the new-client form saves
 * it on the contact, `contacts.referralSourceId`) shows it on the chart's Referral section while
 * that is blank, so the required check does not stop them. `name` is that source's name from the
 * practice's Referral Sources list. A value already on the chart is never replaced; with nothing
 * to fill, the record comes back as it was.
 */
export function fillReferralSource(d, name) {
    const n = (name ?? '').trim();
    if (!n || (d.referral?.referralSource ?? '').trim())
        return d;
    return { ...d, referral: { ...(d.referral ?? {}), referralSource: n } };
}
//# sourceMappingURL=referral.js.map